import React, { useState } from 'react';
import Portlet from '@ui/portlet/Portlet';
import Tabs from '@ui/tabs/Tabs';
import FormInputText from '@ui/form/input/FormInputText';
import FormButton from '@ui/button/FormButton';
import Message from '@ui/message/Message';
import { Integration } from '@app/model/integration-settings/Integration';
import { TYPE_LABEL, TYPE_TEXT } from '../IntegrationSettings.utilities';
import IntegrationHeaderActions from './IntegrationHeaderActions';
import IntegrationCardFooter from './IntegrationCardFooter';
import CredentialField from './CredentialField';
import ReservationsBackfillTab from './ReservationsBackfillTab';
import useIntegrationCard from './useIntegrationCard';
import IntegrationRoomCatalogue from './IntegrationRoomCatalogue';
import { ApiTimezoneField, HistoryField, IntervalField, MonthsAheadField, ProviderField, StartAtField } from './IntegrationFields';

type Tab = 'SETTINGS' | 'BACKFILL';

// Reservations: two tabs. Settings holds the connection and the schedule of the change checks; Backfill holds the
// manual import of the history, whose rules come from the provider (decision 20).
const ReservationsCard: React.FunctionComponent<{ integration: Integration, onOpenLog: () => void }> = ({ integration, onOpenLog }) => {
	const card = useIntegrationCard(integration);
	const [tab, setTab] = useState<Tab>('SETTINGS');
	const isSetUp = integration.status !== 'NOT_SET_UP';

	const settings = <>
		<form className="grid grid-cols-1 gap-6 sm:grid-cols-3" autoComplete="off">
			<ProviderField form={card.form} integration={integration} />
			<FormInputText form={card.form} name="hotelCode" disabled={integration.hotelCode !== null} label="5stelle hotel code" isRequired />
			<CredentialField form={card.form} label="Client token" providerLabel="5stelle" credential={integration.credential}
											isReplacing={card.isReplacing} onReplace={card.setIsReplacing} />
			<ApiTimezoneField form={card.form} />
			<StartAtField form={card.form} />
			<HistoryField form={card.form} />
			<MonthsAheadField form={card.form} type="RESERVATIONS" />
			<div>
				<IntervalField form={card.form} label="Check for changes every" integration={integration} />
				<p className="help">{isSetUp ? `From ${card.form.watch('startAt')}, the time of the property.` : 'Change checks start after the initial backfill.'}</p>
			</div>
		</form>
		{!isSetUp && <div className="mt-6">
			<Message state="info" title="Save, fetch the room catalogue, then switch on and import history">
				Each request covers at most 3 calendar months. Stays and cancellations share 3 calls in any 55 minutes.
				Scheduled change checks start after the initial backfill.
			</Message>
		</div>}
		<p className="mt-6 text-sm text-gray-500">Test connection reads the room types of the property. It uses the room-types daily allowance, separate from the reservation-data allowance.</p>
		<IntegrationRoomCatalogue integration={integration} isBusy={card.isBusy} isRefreshing={card.refreshRooms.isPending} isSharedRefreshing={card.catalogueRefreshing && !card.refreshRooms.isPending}
                isDirty={card.form.formState.isDirty} result={card.refreshRooms.data} error={card.refreshRooms.error}
                onRefresh={() => card.refreshRooms.mutate()} />
		<IntegrationCardFooter integration={integration} onOpenLog={onOpenLog} buttons={<>
			<FormButton state="white" onClick={card.onTest} disabled={card.isBusy}>Test connection</FormButton>
			{(card.form.formState.isDirty || !isSetUp) && <FormButton state="primary" onClick={card.onSave} disabled={card.isBusy}>Save</FormButton>}
		</>} />
	</>;

	return <div id="reservations" className="scroll-mt-6">
		<Portlet isLarge title={TYPE_LABEL.RESERVATIONS}
						actions={<IntegrationHeaderActions integration={integration} onToggle={card.onToggle} isSaving={card.isBusy} />}>
			<p className="text-sm text-gray-500">{TYPE_TEXT.RESERVATIONS} </p>
			<div className="mt-4 mb-6">
				<Tabs<Tab> value={tab} onChange={setTab} options={isSetUp
					? [{ label: 'Settings', value: 'SETTINGS' }, { label: 'Backfill', value: 'BACKFILL' }]
					: [{ label: 'Settings', value: 'SETTINGS' }]} />
			</div>
			{tab === 'SETTINGS' ? settings : <ReservationsBackfillTab integration={integration} />}
		</Portlet>
	</div>;
};

export default ReservationsCard;
